import { FormEvent, useEffect, useState } from 'react';
import {
  createPortfolioCategory,
  deletePortfolioCategory,
  fetchAdminPortfolioCategories,
  reorderPortfolioCategories,
  updatePortfolioCategory,
} from '../adminService';
import { invalidatePortfolioCategories } from '../../Portfolio/portfolioService';
import { PortfolioCategory } from '../../Portfolio/portfolio.types';
import styles from './AdminPortfolio.module.scss';

interface Props {
  token: string;
  itemCounts: Map<string, number>;
  // 이름이 바뀌면 포트폴리오 목록의 카테고리 표시도 바뀌므로 부모가 목록을 다시 불러오게 한다
  onChanged: () => void;
}

function AdminPortfolioCategories({ token, itemCounts, onChanged }: Props) {
  const [categories, setCategories] = useState<PortfolioCategory[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newId, setNewId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const applyList = (list: PortfolioCategory[]) => {
    setCategories(list);
    setDrafts(Object.fromEntries(list.map((c) => [c.id, c.label])));
  };

  const load = () =>
    fetchAdminPortfolioCategories(token)
      .then(applyList)
      .catch((err) => setError(err instanceof Error ? err.message : '카테고리를 불러오지 못했습니다.'));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const run = async (action: () => Promise<unknown>, reloadItems = false) => {
    setError(null);
    setIsBusy(true);
    try {
      await action();
      await load();
      invalidatePortfolioCategories();
      if (reloadItems) onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : '처리 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    if (!newLabel.trim()) {
      setError('카테고리 이름을 입력해주세요.');
      return;
    }
    run(async () => {
      await createPortfolioCategory(token, { id: newId.trim() || undefined, label: newLabel.trim() });
      setNewId('');
      setNewLabel('');
    });
  };

  const handleRename = (category: PortfolioCategory) => {
    const label = (drafts[category.id] ?? '').trim();
    if (!label) {
      setError('카테고리 이름을 입력해주세요.');
      return;
    }
    run(() => updatePortfolioCategory(token, category.id, label), true);
  };

  const handleMove = (index: number, offset: -1 | 1) => {
    if (!categories) return;
    const ids = categories.map((c) => c.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(() => reorderPortfolioCategories(token, ids));
  };

  const handleDelete = (category: PortfolioCategory) => {
    if (!window.confirm(`"${category.label}" 카테고리를 삭제하시겠습니까?`)) return;
    run(() => deletePortfolioCategory(token, category.id));
  };

  return (
    <div className="section">
      <div className="sec-head">
        <div className="sec-title">카테고리 관리</div>
        <div className="sec-actions">
          <span className="chip active">전체 {categories?.length ?? 0}개</span>
        </div>
      </div>

      {error && (
        <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>순서</th>
                <th>ID (URL)</th>
                <th>카테고리 이름</th>
                <th>상품 수</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {!categories && (
                <tr>
                  <td colSpan={5} className="emptyRow">
                    불러오는 중...
                  </td>
                </tr>
              )}
              {categories && categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="emptyRow">
                    등록된 카테고리가 없습니다.
                  </td>
                </tr>
              )}
              {categories?.map((category, index) => {
                const draft = drafts[category.id] ?? category.label;
                const isDirty = draft.trim() !== category.label;
                const count = itemCounts.get(category.id) ?? 0;
                return (
                  <tr key={category.id}>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="위로"
                          disabled={isBusy || index === 0}
                          onClick={() => handleMove(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="아래로"
                          disabled={isBusy || index === categories.length - 1}
                          onClick={() => handleMove(index, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="idcell">{category.id}</span>
                    </td>
                    <td>
                      <input
                        className={styles.categoryInput}
                        value={draft}
                        maxLength={50}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && isDirty) handleRename(category);
                        }}
                      />
                    </td>
                    <td>{count}건</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={isBusy || !isDirty}
                          onClick={() => handleRename(category)}
                        >
                          이름 저장
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline ${styles.dangerBtn}`}
                          disabled={isBusy || count > 0}
                          title={count > 0 ? '상품이 있는 카테고리는 삭제할 수 없습니다.' : undefined}
                          onClick={() => handleDelete(category)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <form className={styles.categoryAddRow} onSubmit={handleAdd}>
          <input
            className={styles.categoryInput}
            value={newLabel}
            maxLength={50}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="새 카테고리 이름 (예: 선물박스)"
          />
          <input
            className={styles.categoryInput}
            value={newId}
            maxLength={30}
            onChange={(e) => setNewId(e.target.value)}
            placeholder="ID (영문 소문자·숫자·-, 비우면 자동 생성)"
          />
          <button type="submit" className="btn btn-primary" disabled={isBusy}>
            + 카테고리 추가
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminPortfolioCategories;
